import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { formatCurrency, formatNormalized } from '../utils/format';
import PriceHistoryChart from '../components/PriceHistoryChart';
import PriceAlerts from '../components/PriceAlerts';
import Loading from '../components/Loading';
import Toast from '../components/Toast';
import SortIcon from '../components/SortIcon';
import { TrendIcon, ChevronIcon } from '../components/Icons';
import AnalyticsQA from '../components/AnalyticsQA';

/**
 * Analytics page showing vendor analysis, category drill-down, budget vs actual,
 * vendor performance, misc charges, cross-vendor price comparison, and month-over-month.
 *
 * @returns {JSX.Element} The analytics page
 */
function Analytics() {
  const [vendorData, setVendorData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const initialLoadDone = useRef(false);
  const [toast, setToast] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [activePreset, setActivePreset] = useState('last_90');

  // Sorting state
  const [vendorSort, setVendorSort] = useState({ key: 'total_spent', dir: 'desc' });
  const [categorySort, setCategorySort] = useState({ key: 'total_spent', dir: 'desc' });

  // Category drill-down state
  const [drillPath, setDrillPath] = useState([]);
  const [parentCategoryId, setParentCategoryId] = useState(null);

  // Budget vs Actual
  const [budgetData, setBudgetData] = useState([]);
  const [budgetLoading, setBudgetLoading] = useState(false);

  // Vendor Performance
  const [vendorPerf, setVendorPerf] = useState([]);
  const [vendorPerfLoading, setVendorPerfLoading] = useState(false);
  const [vendorPerfSort, setVendorPerfSort] = useState({ key: 'total_spend', dir: 'desc' });

  // Misc Charges
  const [miscCharges, setMiscCharges] = useState([]);
  const [miscLoading, setMiscLoading] = useState(false);
  const [expandedMisc, setExpandedMisc] = useState({});

  // Cross-vendor comparison
  const [comparisonItem, setComparisonItem] = useState('');
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Item price history
  const [selectedPriceItem, setSelectedPriceItem] = useState(null);

  // Month-over-Month
  const [momData, setMomData] = useState(null);
  const [momLoading, setMomLoading] = useState(false);

  const [filters, setFilters] = useState({
    business_id: '',
    vendor_id: '',
    start_date: '',
    end_date: '',
    level: '1'
  });

  /**
   * Set the date filter to a quick preset range.
   *
   * @param {string} preset - One of 'last_7', 'last_30', 'last_90', 'ytd', 'all_time'
   */
  const setDatePreset = (preset) => {
    const today = new Date();
    let start = '';
    let end = '';
    const fmt = (d) => d.toISOString().split('T')[0];

    switch (preset) {
      case 'last_7':
        start = fmt(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
        end = fmt(today);
        break;
      case 'last_30':
        start = fmt(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000));
        end = fmt(today);
        break;
      case 'last_90':
        start = fmt(new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000));
        end = fmt(today);
        break;
      case 'ytd':
        start = fmt(new Date(today.getFullYear(), 0, 1));
        end = fmt(today);
        break;
      case 'all_time':
        start = '';
        end = '';
        break;
      default:
        break;
    }

    setActivePreset(preset);
    setFilters(prev => ({ ...prev, start_date: start, end_date: end }));
  };

  /**
   * Build clean params object from filters, removing empty values.
   * @returns {Object} Cleaned params
   */
  const cleanParams = useCallback(() => {
    const params = { ...filters };
    Object.keys(params).forEach(key => !params[key] && delete params[key]);
    return params;
  }, [filters]);

  /**
   * Load main analytics data (vendor + category breakdowns).
   */
  const loadAnalytics = useCallback(async () => {
    // Only show full-page loader on initial load, not drill-down refreshes
    if (!initialLoadDone.current) {
      setIsLoading(true);
    }
    setLoadError(null);
    try {
      const params = cleanParams();
      if (parentCategoryId) {
        params.parent_category_id = parentCategoryId;
      }

      const [vendorRes, categoryRes] = await Promise.all([
        api.getAnalyticsByVendor(params),
        api.getAnalyticsByCategory(params)
      ]);

      setVendorData(vendorRes.data);
      setCategoryData(categoryRes.data);
      setIsLoading(false);
      initialLoadDone.current = true;
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoadError('Failed to load analytics data');
      setToast({ message: 'Failed to load analytics data', type: 'error' });
      setIsLoading(false);
    }
  }, [cleanParams, parentCategoryId]);

  /**
   * Load budget vs actual data.
   */
  const loadBudgetData = useCallback(async () => {
    setBudgetLoading(true);
    try {
      const params = cleanParams();
      delete params.level;
      const res = await api.getBudgetVsActual(params);
      setBudgetData(res.data || []);
    } catch (error) {
      console.error('Error loading budget data:', error);
    } finally {
      setBudgetLoading(false);
    }
  }, [cleanParams]);

  /**
   * Load vendor performance metrics.
   */
  const loadVendorPerformance = useCallback(async () => {
    setVendorPerfLoading(true);
    try {
      const params = cleanParams();
      delete params.level;
      const res = await api.getVendorPerformance(params);
      setVendorPerf(res.data || []);
    } catch (error) {
      console.error('Error loading vendor performance:', error);
    } finally {
      setVendorPerfLoading(false);
    }
  }, [cleanParams]);

  /**
   * Load misc charges analytics.
   */
  const loadMiscCharges = useCallback(async () => {
    setMiscLoading(true);
    try {
      const params = cleanParams();
      delete params.level;
      const res = await api.getMiscChargesAnalytics(params);
      setMiscCharges(res.data || []);
    } catch (error) {
      console.error('Error loading misc charges:', error);
    } finally {
      setMiscLoading(false);
    }
  }, [cleanParams]);

  /**
   * Load month-over-month comparison data.
   */
  const loadMoM = useCallback(async () => {
    setMomLoading(true);
    try {
      const params = cleanParams();
      delete params.level;
      params.comparison = 'mom';
      const res = await api.getAnalyticsSummary(params);
      setMomData(res.data || null);
    } catch (error) {
      console.error('Error loading MoM data:', error);
    } finally {
      setMomLoading(false);
    }
  }, [cleanParams]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    loadBudgetData();
    loadVendorPerformance();
    loadMiscCharges();
    loadMoM();
  }, [loadBudgetData, loadVendorPerformance, loadMiscCharges, loadMoM]);

  /**
   * Sync uncategorized line items with SKU catalog categories, then refresh analytics.
   */
  const handleSyncCategories = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await api.syncLineItemCategories();
      const { updated, total_uncategorized_before, total_uncategorized_after } = res.data;
      setSyncMessage({
        type: 'success',
        text: `Synced ${updated} line item${updated !== 1 ? 's' : ''}. Uncategorized: ${total_uncategorized_before} -> ${total_uncategorized_after}`
      });
      loadAnalytics();
    } catch (error) {
      console.error('Error syncing categories:', error);
      setSyncMessage({ type: 'error', text: 'Failed to sync categories' });
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Handle category drill-down. Clicking a category with children expands to show child categories.
   * @param {Object} category - The category row that was clicked
   */
  const handleCategoryDrillDown = (category) => {
    if (category.has_children || category.child_count > 0) {
      setDrillPath(prev => [...prev, { id: parentCategoryId, name: category.category, level: filters.level }]);
      setParentCategoryId(category.category_id);
      setFilters(prev => ({ ...prev, level: String(Number(prev.level) + 1) }));
    }
  };

  /**
   * Navigate back in the category breadcrumb trail.
   * @param {number} index - The breadcrumb index to go back to (-1 for root)
   */
  const handleBreadcrumbClick = (index) => {
    if (index < 0) {
      setDrillPath([]);
      setParentCategoryId(null);
      setFilters(prev => ({ ...prev, level: '1' }));
    } else {
      const target = drillPath[index];
      setDrillPath(prev => prev.slice(0, index));
      setParentCategoryId(target.id);
      setFilters(prev => ({ ...prev, level: target.level }));
    }
  };

  /**
   * Load cross-vendor price comparison data for a specific item.
   */
  const handleCompareItem = async () => {
    if (!comparisonItem.trim()) return;
    setComparisonLoading(true);
    try {
      const res = await api.getVendorComparison({ item_name: comparisonItem.trim() });
      setComparisonData(res.data || []);
    } catch (error) {
      console.error('Error loading comparison:', error);
      setToast({ message: 'Failed to load comparison data', type: 'error' });
    } finally {
      setComparisonLoading(false);
    }
  };

  /**
   * Generic sorting function for table data.
   * @param {Array} data - The array to sort
   * @param {{ key: string, dir: string }} sortState - Sort key and direction
   * @returns {Array} Sorted copy of the array
   */
  const sortData = (data, sortState) => {
    const { key, dir } = sortState;
    return [...data].sort((a, b) => {
      const aVal = a[key] ?? 0;
      const bVal = b[key] ?? 0;
      if (typeof aVal === 'string') {
        return dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return dir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  /**
   * Toggle sort direction for a column, or set a new sort column.
   * @param {Object} currentSort - Current sort state
   * @param {Function} setSortFn - State setter for sort
   * @param {string} key - Column key to sort by
   */
  const toggleSort = (currentSort, setSortFn, key) => {
    setSortFn(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'desc' }
    );
  };

  /**
   * Render a sortable column header.
   * @param {string} label - Display label
   * @param {string} key - Sort key
   * @param {Object} sortState - Current sort state
   * @param {Function} setSortFn - State setter
   * @param {string} [className] - Optional CSS class
   * @returns {JSX.Element}
   */
  const SortableHeader = ({ label, sortKey, sortState, setSortFn, className }) => (
    <th
      scope="col"
      className={`sort ${className || ''}`}
      onClick={() => toggleSort(sortState, setSortFn, sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      aria-sort={sortState.key === sortKey ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {sortState.key === sortKey && (
        <SortIcon direction={sortState.dir} />
      )}
    </th>
  );

  const totalSpend = vendorData.reduce((sum, v) => sum + v.total_spent, 0);

  const sortedVendors = useMemo(() => sortData(vendorData, vendorSort), [vendorData, vendorSort]);
  const sortedCategories = useMemo(() => sortData(categoryData, categorySort), [categoryData, categorySort]);
  const sortedVendorPerf = useMemo(() => sortData(vendorPerf, vendorPerfSort), [vendorPerf, vendorPerfSort]);

  if (isLoading) {
    return (
      <div className="page">
        <h1>Analytics</h1>
        <Loading variant="spinner" message="Loading analytics data..." />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="page">
        <h1>Analytics</h1>
        <div className="load-error">
          <p className="load-error__message">{loadError}</p>
          <button className="load-error__retry" onClick={loadAnalytics}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Analytics</h1>
        <p className="page-subtitle">Detailed breakdown of spending by vendor and category</p>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="field">
          <label className="label" htmlFor="analytics-start-date">Start Date</label>
          <input
            id="analytics-start-date"
            className="input"
            type="date"
            value={filters.start_date}
            onChange={(e) => { setActivePreset(null); setFilters(prev => ({ ...prev, start_date: e.target.value })); }}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="analytics-end-date">End Date</label>
          <input
            id="analytics-end-date"
            className="input"
            type="date"
            value={filters.end_date}
            onChange={(e) => { setActivePreset(null); setFilters(prev => ({ ...prev, end_date: e.target.value })); }}
          />
        </div>
      </div>

      {/* Quick Date Range Buttons */}
      <div className="quick-date-buttons">
        {[
          { key: 'last_7', label: 'Last 7 Days' },
          { key: 'last_30', label: 'Last 30 Days' },
          { key: 'last_90', label: 'Last 90 Days' },
          { key: 'ytd', label: 'YTD' },
          { key: 'all_time', label: 'All Time' },
        ].map(p => (
          <button
            key={p.key}
            className={`btn ${activePreset === p.key ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            onClick={() => setDatePreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Phase 11.1 — Price Alerts. Surfaces normalized $/unit increases
          across vendors. Pinned at the top so cost movement is visible
          before any other analytics drill-down. */}
      <PriceAlerts />

      {/* Month-over-Month Comparison */}
      {momData && momData.prior_period && (
        <div className="card">
          <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Month-over-Month Comparison</h3>
          {momLoading ? (
            <Loading variant="spinner" message="Loading comparison..." />
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Metric</th>
                    <th scope="col" className="text-right">This Period</th>
                    <th scope="col" className="text-right">Prior Period</th>
                    <th scope="col" className="text-right">Change ($)</th>
                    <th scope="col" className="text-right">Change (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Total Spend', current: momData.total_spend, prior: momData.prior_period.total_spend, pctKey: 'total_spend' },
                    { label: 'Invoice Count', current: momData.invoice_count, prior: momData.prior_period.invoice_count, pctKey: 'invoice_count', isCurrency: false },
                    { label: 'Avg Invoice', current: momData.avg_invoice, prior: momData.prior_period.avg_invoice, pctKey: 'avg_invoice' }
                  ].map((row, i) => {
                    const dollarChange = (row.current || 0) - (row.prior || 0);
                    const pctChange = momData.percent_change?.[row.pctKey] || 0;
                    const isCurrency = row.isCurrency !== false;
                    return (
                      <tr key={i}>
                        <td><strong>{row.label}</strong></td>
                        <td className="text-right">{isCurrency ? <span className="td-amt">{formatCurrency(row.current)}</span> : row.current}</td>
                        <td className="text-right">{isCurrency ? <span className="td-amt">{formatCurrency(row.prior)}</span> : row.prior}</td>
                        <td className="text-right" style={{
                          color: dollarChange > 0 ? 'var(--error)' : dollarChange < 0 ? 'var(--success)' : 'inherit'
                        }}>
                          {isCurrency ? <span className="td-amt">{formatCurrency(dollarChange)}</span> : dollarChange}
                        </td>
                        <td className="text-right" style={{
                          color: pctChange > 0 ? 'var(--error)' : pctChange < 0 ? 'var(--success)' : 'inherit'
                        }}>
                          {pctChange > 0 ? '+' : ''}{pctChange.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Vendor Analysis */}
      <div className="card">
        <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Vendor Analysis</h3>
        {vendorData.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state__icon">&#127978;</span>
            <span className="empty-state__title">No vendor data</span>
            <span className="empty-state__description">
              Add some invoices to see vendor analytics.
            </span>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <SortableHeader label="Vendor" sortKey="name" sortState={vendorSort} setSortFn={setVendorSort} />
                  <SortableHeader label="Total Spent" sortKey="total_spent" sortState={vendorSort} setSortFn={setVendorSort} className="text-right" />
                  {/* Ruth UAT 2026-05-28 (GH #161): combined Extra Fees column
                      (fuel + misc/service charges) lets the cross-vendor view
                      compare extras as one category. The fuel-only column is
                      kept for backwards compat / explicit drill-down. */}
                  <SortableHeader label="Extra Fees" sortKey="total_extra_fees" sortState={vendorSort} setSortFn={setVendorSort} className="text-right" />
                  <SortableHeader label="Fuel Surcharge" sortKey="total_fuel_surcharge" sortState={vendorSort} setSortFn={setVendorSort} className="text-right" />
                  <SortableHeader label="Invoices" sortKey="invoice_count" sortState={vendorSort} setSortFn={setVendorSort} className="text-right" />
                  <th scope="col" className="text-right">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {sortedVendors.map((v, i) => (
                  <tr key={i}>
                    <td>
                      <Link to={`../invoices?vendor_id=${v.vendor_id || ''}`}>
                        {v.name}
                      </Link>
                    </td>
                    <td className="text-right"><span className="td-amt">{formatCurrency(v.total_spent)}</span></td>
                    <td
                      className="text-right"
                      title={`Fuel surcharge ${formatCurrency(v.total_fuel_surcharge || 0)} + misc/service charges ${formatCurrency(v.total_misc_charges || 0)}`}
                    >
                      <span className="td-amt">{formatCurrency(v.total_extra_fees || 0)}</span>
                    </td>
                    <td className="text-right"><span className="td-amt">{formatCurrency(v.total_fuel_surcharge || 0)}</span></td>
                    <td className="text-right">{v.invoice_count}</td>
                    <td className="text-right">{totalSpend > 0 ? ((v.total_spent / totalSpend) * 100).toFixed(1) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Category Analysis with Drill-Down */}
      <div className="card">
        <div className="settings-form__table-header">
          <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Category Analysis</h3>
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleSyncCategories}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Categories'}
          </button>
        </div>

        {/* Breadcrumb navigation for drill-down */}
        {drillPath.length > 0 && (
          <div className="breadcrumb" style={{ marginBottom: 'var(--sp2)' }}>
            <button className="btn btn-ghost btn-xs" onClick={() => handleBreadcrumbClick(-1)}>
              All Categories
            </button>
            {drillPath.map((crumb, i) => (
              <span key={i}>
                <span className="bc-sep"> / </span>
                <button className="btn btn-ghost btn-xs" onClick={() => handleBreadcrumbClick(i)}>
                  {crumb.name}
                </button>
              </span>
            ))}
          </div>
        )}

        {syncMessage && (
          <div className={`sync-feedback sync-feedback--${syncMessage.type}`}>
            {syncMessage.text}
          </div>
        )}

        {categoryData.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state__icon">&#128193;</span>
            <span className="empty-state__title">No category data</span>
            <span className="empty-state__description">
              Add some invoices with categorized items to see category analytics.
            </span>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <SortableHeader label="Category" sortKey="category" sortState={categorySort} setSortFn={setCategorySort} />
                  <SortableHeader label="Total Spent" sortKey="total_spent" sortState={categorySort} setSortFn={setCategorySort} className="text-right" />
                  <SortableHeader label="Items" sortKey="item_count" sortState={categorySort} setSortFn={setCategorySort} className="text-right" />
                  <th scope="col" className="text-right">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {sortedCategories.map((c, i) => (
                  <tr
                    key={i}
                    onClick={() => handleCategoryDrillDown(c)}
                    style={{ cursor: (c.has_children || c.child_count > 0) ? 'pointer' : 'default' }}
                    title={(c.has_children || c.child_count > 0) ? 'Click to drill down' : ''}
                  >
                    <td>
                      {c.category}
                      {(c.has_children || c.child_count > 0) && (
                        <span style={{ marginLeft: 'var(--sp2)', opacity: 0.5 }}>
                          ({c.child_count || 0} sub)
                        </span>
                      )}
                    </td>
                    <td className="text-right"><span className="td-amt">{formatCurrency(c.total_spent)}</span></td>
                    <td className="text-right">{c.item_count}</td>
                    <td className="text-right">{totalSpend > 0 ? ((c.total_spent / totalSpend) * 100).toFixed(1) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Budget vs Actual */}
      <div className="card">
        <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Budget vs Actual</h3>
        {budgetLoading ? (
          <Loading variant="spinner" message="Loading budget data..." />
        ) : budgetData.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state__icon">&#128176;</span>
            <span className="empty-state__title">No budgets configured</span>
            <span className="empty-state__description">
              Set up budgets in the Setup page to track spending against targets.
            </span>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Category / Vendor</th>
                  <th scope="col">Period</th>
                  <th scope="col" className="text-right">Budget</th>
                  <th scope="col" className="text-right">Actual</th>
                  <th scope="col" className="text-right">Variance ($)</th>
                  <th scope="col" className="text-right">Variance (%)</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {budgetData.map((row, i) => {
                  const variance = row.variance ?? ((row.budget_amount || 0) - (row.actual_spend || 0));
                  const variancePct = row.variance_pct ?? (row.budget_amount > 0
                    ? ((variance / row.budget_amount) * 100)
                    : 0);
                  const isOver = variance < 0;
                  return (
                    <tr key={i}>
                      <td>{row.category_path || row.vendor_name || 'N/A'}</td>
                      <td>{row.period_start ? new Date(row.period_start + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short' }) : '—'}</td>
                      <td className="text-right"><span className="td-amt">{formatCurrency(row.budget_amount)}</span></td>
                      <td className="text-right"><span className="td-amt">{formatCurrency(row.actual_spend)}</span></td>
                      <td className="text-right" style={{
                        color: isOver ? 'var(--error)' : 'var(--success)'
                      }}>
                        <span className="td-amt">{formatCurrency(variance)}</span>
                      </td>
                      <td className="text-right" style={{
                        color: isOver ? 'var(--error)' : 'var(--success)'
                      }}>
                        {variancePct.toFixed(1)}%
                      </td>
                      <td>
                        <span className="badge" style={{ color: isOver ? 'var(--error)' : 'var(--success)' }}>
                          {isOver ? 'Over Budget' : 'Under Budget'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Vendor Performance */}
      <div className="card">
        <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Vendor Performance</h3>
        {vendorPerfLoading ? (
          <Loading variant="spinner" message="Loading vendor performance..." />
        ) : vendorPerf.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state__icon">&#128200;</span>
            <span className="empty-state__title">No vendor performance data</span>
            <span className="empty-state__description">
              Add invoices from multiple vendors to see performance metrics.
            </span>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <SortableHeader label="Vendor" sortKey="vendor_name" sortState={vendorPerfSort} setSortFn={setVendorPerfSort} />
                  <SortableHeader label="Total Spend" sortKey="total_spend" sortState={vendorPerfSort} setSortFn={setVendorPerfSort} className="text-right" />
                  <SortableHeader label="Avg Invoice" sortKey="avg_invoice_amount" sortState={vendorPerfSort} setSortFn={setVendorPerfSort} className="text-right" />
                  <SortableHeader label="Invoices" sortKey="invoice_count" sortState={vendorPerfSort} setSortFn={setVendorPerfSort} className="text-right" />
                  <SortableHeader label="Avg Days Between" sortKey="invoice_frequency_days" sortState={vendorPerfSort} setSortFn={setVendorPerfSort} className="text-right" />
                  <th scope="col" className="text-right">Cost Trend</th>
                  <th scope="col" className="text-right">Spend Share</th>
                </tr>
              </thead>
              <tbody>
                {sortedVendorPerf.map((v, i) => {
                  return (
                    <tr key={i}>
                      <td>
                        <Link to={`../invoices?vendor_id=${v.vendor_id || ''}`}>
                          {v.vendor_name}
                        </Link>
                      </td>
                      <td className="text-right"><span className="td-amt">{formatCurrency(v.total_spend)}</span></td>
                      <td className="text-right"><span className="td-amt">{formatCurrency(v.avg_invoice_amount)}</span></td>
                      <td className="text-right">{v.invoice_count}</td>
                      <td className="text-right">{v.invoice_frequency_days ? `${Math.round(v.invoice_frequency_days)}d` : '-'}</td>
                      <td className="text-right">
                        <TrendIcon direction={v.cost_trend} /> {v.cost_trend ? v.cost_trend.charAt(0).toUpperCase() + v.cost_trend.slice(1) : '-'}
                      </td>
                      <td className="text-right">{v.spending_share_pct != null ? `${v.spending_share_pct.toFixed(1)}%` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Misc Charges Breakdown */}
      <div className="card">
        <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Misc Charges Breakdown</h3>
        {miscLoading ? (
          <Loading variant="spinner" message="Loading misc charges..." />
        ) : miscCharges.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state__icon">&#128221;</span>
            <span className="empty-state__title">No misc charges</span>
            <span className="empty-state__description">
              No miscellaneous charges found for the selected period.
            </span>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Charge Description</th>
                  <th scope="col" className="text-right">Total Amount</th>
                  <th scope="col" className="text-right">Count</th>
                  <th scope="col" className="text-right">Avg Amount</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {miscCharges.map((charge, i) => (
                  <React.Fragment key={i}>
                    <tr
                      onClick={() => setExpandedMisc(prev => ({ ...prev, [i]: !prev[i] }))}
                      style={{ cursor: charge.vendors ? 'pointer' : 'default' }}
                    >
                      <td><strong>{charge.description}</strong></td>
                      <td className="text-right"><span className="td-amt">{formatCurrency(charge.total_amount)}</span></td>
                      <td className="text-right">{charge.count}</td>
                      <td className="text-right"><span className="td-amt">{formatCurrency(charge.avg_amount)}</span></td>
                      <td className="text-right">
                        {charge.vendors && (
                          <ChevronIcon expanded={expandedMisc[i]} />
                        )}
                      </td>
                    </tr>
                    {expandedMisc[i] && charge.vendors && charge.vendors.map((vendor, j) => (
                      <tr key={`${i}-${j}`} style={{ backgroundColor: 'var(--st100)' }}>
                        <td style={{ paddingLeft: '2rem' }}>{vendor.vendor_name}</td>
                        <td className="text-right"><span className="td-amt">{formatCurrency(vendor.amount)}</span></td>
                        <td className="text-right">{vendor.count}</td>
                        <td className="text-right"><span className="td-amt">{formatCurrency(vendor.amount / (vendor.count || 1))}</span></td>
                        <td></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cross-Vendor Price Comparison */}
      <div className="card">
        {/* Phase 11 Wave 6b — Normalized $ explanation hint. Wave 6a backend
            computes per-canonical-unit price via sku_catalog.base_unit +
            base_units_per_pack curation; this hint tells operators where those
            curation fields live (Setup → SKU catalog) so they can populate the
            Normalized $ column. Rows without curation render '—'. */}
        <p className="hint">
          Normalized $ shows price per base unit (e.g. $/egg, $/ml, $/g) so packs of different sizes are directly comparable. Curate Base unit and Base units/pack in Setup → SKU catalog to populate the column; un-curated rows render as &mdash;.
        </p>
        <div className="settings-form__table-header">
          <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            Cross-Vendor Price Comparison
          </h3>
          <div style={{ display: 'flex', gap: 'var(--sp2)', alignItems: 'center' }}>
            <input
              id="comparison-item"
              className="input settings-form__search-input"
              type="text"
              value={comparisonItem}
              onChange={(e) => setComparisonItem(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCompareItem()}
              placeholder="Item name..."
              aria-label="Item name to compare"
              style={{ maxWidth: '220px' }}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleCompareItem}
              disabled={comparisonLoading || !comparisonItem.trim()}
            >
              {comparisonLoading ? 'Searching...' : 'Compare'}
            </button>
          </div>
        </div>
        {comparisonData.length > 0 ? (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Vendor</th>
                  <th scope="col" className="text-right">Latest</th>
                  <th scope="col" className="text-right">Avg</th>
                  <th scope="col" className="text-right">Min</th>
                  <th scope="col" className="text-right">Max</th>
                  {/* Phase 11 Wave 6b — Wave 6a Variant B aggregate columns:
                      avg/min/max_normalized_price with MAX(sc.base_unit) as
                      canonical_unit. NULL when sku_catalog isn't curated. */}
                  <th scope="col" className="text-right">Avg $/unit</th>
                  <th scope="col" className="text-right">Min $/unit</th>
                  <th scope="col" className="text-right">Max $/unit</th>
                  <th scope="col" className="text-right">Last Invoice</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => {
                  const isCheapest = row.latest_price === Math.min(...comparisonData.map(r => r.latest_price));
                  return (
                    <tr key={i}>
                      <td>
                        {row.vendor_name}
                        {isCheapest && comparisonData.length > 1 && (
                          <span className="badge" style={{ marginLeft: 'var(--sp2)', color: 'var(--success)' }}>Best</span>
                        )}
                      </td>
                      <td className="text-right"><span className="td-amt">{formatCurrency(row.latest_price)}</span></td>
                      <td className="text-right"><span className="td-amt">{formatCurrency(row.avg_price)}</span></td>
                      <td className="text-right"><span className="td-amt">{formatCurrency(row.min_price)}</span></td>
                      <td className="text-right"><span className="td-amt">{formatCurrency(row.max_price)}</span></td>
                      {/* Normalized aggregates — em-dash when sku_catalog
                          isn't curated for this row's (vendor, item, sku). */}
                      <td className="text-right"><span className="td-amt">{formatNormalized(row.avg_normalized_price, row)}</span></td>
                      <td className="text-right"><span className="td-amt">{formatNormalized(row.min_normalized_price, row)}</span></td>
                      <td className="text-right"><span className="td-amt">{formatNormalized(row.max_normalized_price, row)}</span></td>
                      <td className="text-right">{row.last_invoice_date}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={(e) => { e.stopPropagation(); setSelectedPriceItem(comparisonItem); }}
                        >
                          History
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <span className="empty-state__title">Search for an item to compare prices across vendors</span>
          </div>
        )}
      </div>

      {/* Price History Chart */}
      {selectedPriceItem && (
        <PriceHistoryChart
          itemName={selectedPriceItem}
          onClose={() => setSelectedPriceItem(null)}
        />
      )}

      {/* Ask COGSLOG AI Q&A */}
      <AnalyticsQA businessId={filters.business_id || null} />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default Analytics;
